import {
  mParticleLogEvent,
  SupportFooter,
  mParticleEventType as EventType,
} from '@treatwell/ui';
import { useTranslation } from 'react-i18next';
import React, { useContext } from 'react';

import { AccountDetailsState } from 'js/model/state/account/details';

import { connect } from 'react-redux';
import { ReduxState } from 'js/model/state';
import { getAccountDetails } from 'js/redux-modules/account/selectors';
import { loadAccountDetails } from 'js/redux-modules/account/details/actions';
import { requestSalesforceTokens } from 'js/service/salesforceService';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Context } from '../LocaleWrapper';
import { salesforceBotConfig, supportChatConfig } from './config';
import { CustomSupportFooter } from '../CustomSupportFooter';

type Props = {
  accountDetails: AccountDetailsState;
  loadAccountDetails: () => void;
  className?: string;
};

const SalesforceFooterInner = ({
  accountDetails,
  loadAccountDetails,
  className,
}: Props) => {
  const { t } = useTranslation();
  const { channel } = useContext(Context);
  const useCustomWidgetSupportchat = useFeatureIsOn(
    'ff-web-support__custom_chat_widget_enabled'
  );
  const strings = {
    genericError: t('referral.otpInputModal.error.generic'),
    helpCentre: t('footer.helpCentre'),
    chatSupport: t('footer.chatSupport'),
    modal: {
      close: t('footer.chatModal.close'),
      title: t('footer.chatModal.title'),
      body: t('footer.chatModal.body'),
      cta: t('footer.chatModal.cta'),
      linkText: t('footer.chatModal.link'),
    },
  };

  const localeLanguage = channel.locale.split('_')[0];
  const customerCareLanguages = [
    'es',
    'fr',
    'de',
    'el',
    'it',
    'lt',
    'nl_NL',
    'en_GB',
    'en_BE',
    'en_IE',
    'en_NL',
    'pt_PT',
  ];
  const language =
    customerCareLanguages.find(
      (l) => l === localeLanguage || l === channel.locale
    ) ?? 'en_GB';

  const helpUrl = `https://customercare.treatwell.com/s/article/How-to-Request-Support-as-a-Guest-User-on-Treatwell?language=${language}`;

  const onHandleBotError = (error: Error) => {
    console.warn(
      useCustomWidgetSupportchat
        ? 'Custom chat error:'
        : 'Possible Salesforce bot e:',
      error
    );
  };

  React.useEffect(() => {
    if (
      !accountDetails.user &&
      !accountDetails.isFetching &&
      !accountDetails.error
    ) {
      loadAccountDetails();
    }
  }, [accountDetails, loadAccountDetails]);

  const preChat = {
    hidden: {
      externalUserId: accountDetails.user?.accountId?.toString() || '0',
      externalUserFullName: `${accountDetails.user?.firstName} ${accountDetails.user?.lastName}`,
      externalUserEmail: accountDetails.user?.email || '',
      externalLanguage: channel.locale,
      externalCountry: channel.country.countryCode,
      externalSource: 'Web',
    },
  };

  const onChatOpen = (conversationId: string | null) => {
    mParticleLogEvent('chat_opened', EventType.Other, {
      country: channel.country.countryCode,
      platform: 'web',
      conversation_id: conversationId,
      chat_source: 'home_footer',
    });
  };

  const onChatClose = (conversationId: string | null) => {
    mParticleLogEvent('chat_closed', EventType.Other, {
      country: channel.country.countryCode,
      platform: 'web',
      conversation_id: conversationId,
      chat_source: 'home_footer',
    });
  };

  const getTokens = async () => {
    try {
      return requestSalesforceTokens();
    } catch {
      return null;
    }
  };

  const { eswConfigDevName, orgId, scrt2URL } = supportChatConfig.init;
  const customChatConfig = {
    bootstrapJsSrc: supportChatConfig.bootstrapJsSrc,
    baseUrl: scrt2URL,
    developerName: eswConfigDevName,
    orgId,
  };
  const userInfo = {
    userId: accountDetails.user?.accountId?.toString() || '0',
    fullName: `${accountDetails.user?.firstName} ${accountDetails.user?.lastName}`,
    email: accountDetails.user?.email || '',
    language: channel.locale,
    country: channel.country.countryCode,
  };
  return (
    <div className={className} data-cy="SalesforceFooter">
      {useCustomWidgetSupportchat ? (
        <CustomSupportFooter
          configuration={customChatConfig}
          getTokens={getTokens}
          supportStrings={strings}
          helpUrl={helpUrl}
          onChatOpen={onChatOpen}
          onChatClose={onChatClose}
          onHandleChatError={onHandleBotError}
          userInfo={userInfo}
          locale={channel.locale}
          userAuthenticated={accountDetails.user?.isAuthenticated || false}
          uiConfig={supportChatConfig.uiConfig}
        />
      ) : (
        <SupportFooter
          botProps={{
            ...salesforceBotConfig,
            locale: channel.locale,
          }}
          preChat={preChat}
          strings={strings}
          helpUrl={helpUrl}
          onChatOpen={onChatOpen}
          onChatClose={onChatClose}
          getTokens={getTokens}
          onHandleBotError={onHandleBotError}
          userAuthenticated={accountDetails.user?.isAuthenticated || false}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  accountDetails: getAccountDetails(state),
});

const mapDispatchToProps = {
  loadAccountDetails,
};

export const SalesforceFooter = connect(
  mapStateToProps,
  mapDispatchToProps
)(SalesforceFooterInner);
